import { useContext } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { useSnackbar } from '@shared/services/SnackbarService';
import { useReviewContext } from '../../review/context/ReviewContext';
import AskQuestionModal from './AskQuestionModal';
import CreateIntelligenceRequestModal from './CreateIntelligenceRequestModal';

interface UseReviewBannerActionsProps {
  reviewId: number;
  companyName: string;
  companyId?: number;
  refetchQuestions?: () => void;
}

export function useReviewBannerActions({
  reviewId,
  companyName,
  companyId,
  refetchQuestions,
}: UseReviewBannerActionsProps) {
  const { user } = useContext(UserContext);
  const { currentReview } = useReviewContext();
  const { openMessage, closeMessage } = useMessageService();
  const { showSnackbar } = useSnackbar();
  const api = useApi();

  const handleEngageAuthorClick = () => {
    openMessage({
      modalType: 'action',
      header: 'Request a Consultation',
      customBody: (
        <CreateIntelligenceRequestModal
          reviewId={reviewId}
          companyId={companyId}
          targetUserId={currentReview?.user?.id}
        />
      ),
      size: 'large',
      confirmLabel: 'Send Request',
      cancelLabel: 'Cancel',
      confirmButtonColor: 'primary',
    });
  };

  const handleAskQuestionClick = () => {
    let questionText = '';
    let isQuestionValid = false;
    let setShowValidation: ((show: boolean) => void) | null = null;

    openMessage({
      modalType: 'action',
      header: 'Ask a Question',
      customBody: (
        <AskQuestionModal
          reviewId={reviewId}
          companyName={companyName}
          reviewAuthor={currentReview?.user}
          onSubmit={(question, showValidationSetter) => {
            questionText = question;
            isQuestionValid = question.trim().length >= 25;
            setShowValidation = showValidationSetter;
          }}
          onTextChange={(question, isValid, showValidationSetter) => {
            questionText = question;
            isQuestionValid = isValid;
            setShowValidation = showValidationSetter;
          }}
          onRequestCall={() => {
            closeMessage();
            handleEngageAuthorClick();
          }}
        />
      ),
      size: 'medium',
      confirmLabel: 'Ask Question',
      cancelLabel: 'Cancel',
      confirmButtonColor: 'primary',
      onConfirm: async () => {
        if (isQuestionValid && questionText.trim()) {
          try {
            await api.post('/question', {
              question: questionText.trim(),
              reviewId,
            });
            showSnackbar('Question sent successfully!', 'success');
            closeMessage();

            if (refetchQuestions) {
              refetchQuestions();
            }

            return true;
          }
          catch {
            showSnackbar('Failed to send question. Please try again.', 'error');
            return false;
          }
        }
        if (setShowValidation) {
          setShowValidation(true);
        }
        return false;
      },
    });
  };

  return {
    handleAskQuestionClick,
    handleEngageAuthorClick,
  };
}
